import {
  ReactNode,
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IApiResponse } from "@miplatform/types";
import {
  CustomerPricingDetailsContextType,
  TabFormState,
} from "./CustomerPricingDetails.types";
import { useCustomerPricingDetailsViewModel } from "./useCustomerPricingDetailsViewModel";

const CustomerPricingDetailsContext =
  createContext<CustomerPricingDetailsContextType | null>(null);

export function CustomerPricingDetailsProvider({
  children,
}: {
  children: ReactNode;
}) {
  // account details
  const { params } = useRouter();
  const { id: accountId } = params;

  const {
    isLoading: isViewModelLoading,
    accountDetails,
    getAccountDetails,
  } = useCustomerPricingDetailsViewModel();

  useEffect(() => {
    if (accountId) {
      getAccountDetails(accountId);
    }
  }, [accountId]);

  // tab form state management and save handling
  const [tabFormState, setTabFormState] = useState<TabFormState>(
    DEFAULT_TAB_FORM_STATE
  );

  const saveHandlerRef = useRef<(() => Promise<IApiResponse>) | null>(null);

  const setSaveHandler = useCallback(
    (handler: (() => Promise<IApiResponse>) | null) => {
      saveHandlerRef.current = handler;
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (saveHandlerRef.current) {
      const response = await saveHandlerRef.current();
      // re-fetch account details upon saving
      if (response.status === HTTP_STATUSES.OK && accountId) {
        getAccountDetails(accountId);
      }
    }
  }, [accountId]);

  const value = useMemo(
    () => ({
      accountDetails,
      getAccountDetails,
      isViewModelLoading,
      tabFormState,
      setTabFormState,
      setSaveHandler,
      handleSave,
    }),
    [
      accountDetails,
      getAccountDetails,
      isViewModelLoading,
      tabFormState,
      setTabFormState,
      setSaveHandler,
      handleSave,
    ]
  );

  return (
    <CustomerPricingDetailsContext value={value}>
      {children}
    </CustomerPricingDetailsContext>
  );
}

export function useCustomerPricingDetailsContext() {
  const context = use(CustomerPricingDetailsContext);
  if (!context) {
    throw new Error(
      "useCustomerPricingDetailsContext must be used within a CustomerPricingDetailsProvider"
    );
  }
  return context;
}

export function useTabFormRegistration<T extends FieldValues>(
  form: UseFormReturn<T>,
  isTabLoading = false
) {
  const { setTabFormState, setSaveHandler } =
    useCustomerPricingDetailsContext();
  const {
    formState: { isDirty, isValid },
  } = form;

  // register tab's formState variables to context
  useEffect(() => {
    setTabFormState({ isDirty, isValid, isLoading: isTabLoading });
  }, [isDirty, isValid, isTabLoading]);

  useEffect(() => {
    // cleanup on unmount
    return () => {
      setTabFormState(DEFAULT_TAB_FORM_STATE);
      setSaveHandler(null);
    };
  }, [setTabFormState, setSaveHandler]);
}

const DEFAULT_TAB_FORM_STATE: TabFormState = {
  isDirty: false,
  isValid: false,
  isLoading: false,
};
